import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import EntityFormFields from '../../../shared/EntityFormFields';
import FormSection from '../../../shared/FormSection';
import SoftwareRiskDialog, { emptySoftwareRiskRow } from '../../../shared/SoftwareRiskDialog';
import {
  DATA_MODEL_EXTRA_FIELDS,
  DATA_MODEL_INFO_FIELDS,
  LOOKUP_CONFIGS,
} from '../../../config/wizardSteps';
import { formatEnumLabel } from '../../../constants/enums';
import { withCompactSelectFields } from '../../../utils/fieldLayout';
import { persistArtificialIntelligenceDetails } from '../../../utils/persistWizardNestedCollection';
import { apiErrorMessage } from '../../../utils/responseHelpers';
import { detailToWizardState } from '../../../utils/wizardStateMapper';
import { toast } from 'react-hot-toast';
import WizardLookupStep from './WizardLookupStep';

const emptyDataModel = () => ({
  dataSourcesUsed: '',
  personalData: false,
  sensitiveDataCategories: [],
  modelType: '',
  trainingDataOrigin: '',
});

const PRIORITY_COLORS = {
  LOW: { bg: '#E8F5E9', color: '#1B5E20' },
  MEDIUM: { bg: '#FFF8E1', color: '#F57F17' },
  HIGH: { bg: '#FBE9E7', color: '#BF360C' },
  CRITICAL: { bg: '#FFEBEE', color: '#B71C1C' },
};

const WizardStepRiskImpact = ({
  values,
  dataModelValues,
  errors,
  touched,
  onChange,
  onSetField,
  onDataModelReplace,
  lookups,
  onRefreshLookups,
  systemName,
  softwareName,
  softwareId,
  onBeforeView,
  onWizardMetaPatch,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const usesAi = Boolean(values?.usesArtificialIntelligence);
  const knownBias = Boolean(values?.knownBias);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDataModel, setDraftDataModel] = useState(emptyDataModel);
  const [biasDialogOpen, setBiasDialogOpen] = useState(false);
  const [draftBiasDescription, setDraftBiasDescription] = useState('');
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [savingAi, setSavingAi] = useState(false);

  const risks = values?.risks ?? [];

  const regulationStep = {
    id: 'regulations',
    label: LOOKUP_CONFIGS.regulations.label,
    lookupKey: LOOKUP_CONFIGS.regulations.lookupKey,
    optionLabel: LOOKUP_CONFIGS.regulations.optionLabel,
    stateKey: 'regulationIds',
  };

  const openAiDialog = (seed = dataModelValues) => {
    setDraftDataModel({ ...emptyDataModel(), ...(seed ?? {}) });
    setDialogOpen(true);
  };

  const openBiasDialog = (seed = values?.biasDescription) => {
    setDraftBiasDescription(seed ?? '');
    setBiasDialogOpen(true);
  };

  const handleAiToggle = (checked) => {
    onSetField('usesArtificialIntelligence', checked);
    if (checked) {
      openAiDialog(dataModelValues);
      return;
    }
    setDialogOpen(false);
    onDataModelReplace?.(emptyDataModel());
  };

  const handleKnownBiasToggle = (checked) => {
    onSetField('knownBias', checked);
    if (checked) {
      openBiasDialog(values?.biasDescription);
      return;
    }
    setBiasDialogOpen(false);
    onSetField('biasDescription', '');
  };

  const captureDataModel = (draft) => ({
    dataSourcesUsed: String(draft?.dataSourcesUsed ?? ''),
    personalData: Boolean(draft?.personalData),
    sensitiveDataCategories: Array.isArray(draft?.sensitiveDataCategories)
      ? [...draft.sensitiveDataCategories]
      : [],
    modelType: draft?.modelType ?? '',
    trainingDataOrigin: draft?.trainingDataOrigin ?? '',
  });

  const handleDialogSave = async () => {
    const nextModel = captureDataModel(draftDataModel);
    onDataModelReplace?.(nextModel);
    onSetField('usesArtificialIntelligence', true);
    if (!softwareId) {
      toast.error('Save system identification first so artificial intelligence details can be stored.');
      return;
    }
    setSavingAi(true);
    try {
      const updated = await persistArtificialIntelligenceDetails(softwareId, nextModel, values);
      const mapped = detailToWizardState(updated);
      onDataModelReplace?.(mapped.dataModel ?? nextModel);
      onWizardMetaPatch?.({
        dataInformationId: mapped.meta?.dataInformationId ?? updated?.dataInformation?.id ?? null,
        dataSourceIds: mapped.meta?.dataSourceIds
          ?? (updated?.dataSources ?? []).map((d) => d.id).filter((id) => id != null),
      });
      toast.success('Artificial intelligence details saved.');
      setDialogOpen(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save artificial intelligence details.'));
    } finally {
      setSavingAi(false);
    }
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleBiasDialogSave = () => {
    const text = (draftBiasDescription || '').trim();
    onSetField('knownBias', true);
    onSetField('biasDescription', text);
    setBiasDialogOpen(false);
  };

  const handleBiasDialogCancel = () => {
    setBiasDialogOpen(false);
    // If user cancels with no existing description, uncheck the box.
    if (!(values?.biasDescription || '').trim()) {
      onSetField('knownBias', false);
      onSetField('biasDescription', '');
    }
  };

  const openAddRisk = () => {
    setEditingRisk(emptySoftwareRiskRow());
    setRiskDialogOpen(true);
  };

  const openEditRisk = (row) => {
    setEditingRisk({ ...emptySoftwareRiskRow(), ...row });
    setRiskDialogOpen(true);
  };

  const openViewRisk = (row) => {
    onBeforeView?.();
    navigate('/system-register/software-risks/view', {
      state: {
        risk: row,
        softwareName: softwareName || systemName || '',
        softwareId: softwareId ?? null,
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  };

  const handleRiskSave = (row) => {
    const list = [...(values.risks ?? [])];
    const idx = list.findIndex((r) => r.key === row.key || (row.id != null && r.id === row.id));
    if (idx >= 0) {
      list[idx] = row;
    } else {
      list.push(row);
    }
    onSetField('risks', list);
    setRiskDialogOpen(false);
    setEditingRisk(null);
  };

  const handleRiskDelete = (row) => {
    onSetField(
      'risks',
      (values.risks ?? []).filter((r) => r.key !== row.key && !(row.id != null && r.id === row.id))
    );
  };

  const draftFieldProps = {
    values: draftDataModel,
    errors: {},
    touched: {},
    handleChange: (e) =>
      setDraftDataModel((prev) => ({ ...prev, [e.target.name]: e.target.value })),
    handleBlur: () => {},
    setFieldValue: (name, val) => setDraftDataModel((prev) => ({ ...prev, [name]: val })),
    columns: 1,
    dense: false,
  };

  const hasAiDataFilled = Boolean(
    (dataModelValues?.dataSourcesUsed ?? '').trim()
      || dataModelValues?.personalData
      || (dataModelValues?.sensitiveDataCategories?.length ?? 0) > 0
      || dataModelValues?.modelType
      || dataModelValues?.trainingDataOrigin
  );

  return (
    <Box display="flex" flexDirection="column" gap={2.5} pt={0.5}>
      <Box
        display="flex"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <FormControlLabel
          sx={{ m: 0, flexShrink: 0 }}
          control={
            <Checkbox
              checked={usesAi}
              onChange={(e) => handleAiToggle(e.target.checked)}
              size="small"
            />
          }
          label="This system uses Artificial Intelligence"
        />
        <Box sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={knownBias}
                onChange={(e) => handleKnownBiasToggle(e.target.checked)}
                size="small"
              />
            }
            label="Known bias risk"
          />
          {errors?.knownBias ? (
            <Typography variant="caption" color="error" display="block">
              {errors.knownBias}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {knownBias ? (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box>
            <Button variant="contained" color="secondary" onClick={() => openBiasDialog()}>
              {(values?.biasDescription || '').trim()
                ? 'Edit bias risk description'
                : 'Describe bias risk'}
            </Button>
          </Box>
          {(values?.biasDescription || '').trim() ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
                Known bias risk
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {values.biasDescription}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Describe the particular bias risk so it appears on the report.
            </Typography>
          )}
        </Box>
      ) : null}

      {usesAi ? (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box>
            <Button variant="contained" onClick={() => openAiDialog()}>
              {hasAiDataFilled ? 'Edit Data & Model Information' : 'Fill Data & Model Information'}
            </Button>
          </Box>
          {errors?.usesArtificialIntelligence ? (
            <Typography variant="caption" color="error">
              {errors.usesArtificialIntelligence}
            </Typography>
          ) : null}
          {hasAiDataFilled ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.75}>
                Artificial intelligence details
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Data sources:</strong>{' '}
                {(dataModelValues?.dataSourcesUsed || '').trim() || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Personal data:</strong> {dataModelValues?.personalData ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Sensitive categories:</strong>{' '}
                {(dataModelValues?.sensitiveDataCategories ?? []).length
                  ? dataModelValues.sensitiveDataCategories
                      .map((c) => String(c).replace(/_/g, ' '))
                      .join(', ')
                  : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Model type:</strong>{' '}
                {dataModelValues?.modelType
                  ? String(dataModelValues.modelType).replace(/_/g, ' ')
                  : '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Training data origin:</strong>{' '}
                {dataModelValues?.trainingDataOrigin
                  ? String(dataModelValues.trainingDataOrigin).replace(/_/g, ' ')
                  : '—'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Complete Data & Model Information so AI details appear on the report.
            </Typography>
          )}
        </Box>
      ) : null}

      <Box sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}>
        <WizardLookupStep
          step={regulationStep}
          selectedIds={values.regulationIds}
          onChange={(ids) => onSetField('regulationIds', ids)}
          lookups={lookups}
          onRefreshLookups={onRefreshLookups}
          assignmentTargetName={systemName}
          errors={errors}
        />
      </Box>

      <FormSection
        title="Risk register"
        headerAside={
          <Box display="flex" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddRisk}
              fullWidth
              sx={{ width: { sm: 'auto' } }}
            >
              Add risk
            </Button>
          </Box>
        }
      >
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderColor: alpha(theme.palette.primary.main, 0.16) }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Risk name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Resources impacted</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {risks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                      No risks added yet. Click Add risk to register one or more risks for this system.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                risks.map((row) => {
                  const priorityTone = PRIORITY_COLORS[row.riskPriority] || {
                    bg: '#ECEFF1',
                    color: '#455A64',
                  };
                  return (
                    <TableRow key={row.key || row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.riskName || '—'}
                        </Typography>
                        {row.riskDescription ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{
                              maxWidth: 280,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.riskDescription}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{formatEnumLabel(row.riskCategory) || '—'}</TableCell>
                      <TableCell>{row.resourcesImpacted || '—'}</TableCell>
                      <TableCell>
                        {row.riskPriority ? (
                          <Chip
                            size="small"
                            label={formatEnumLabel(row.riskPriority)}
                            sx={{
                              bgcolor: priorityTone.bg,
                              color: priorityTone.color,
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{row.riskOwner || '—'}</TableCell>
                      <TableCell>{formatEnumLabel(row.status) || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View risk form">
                          <IconButton size="small" onClick={() => openViewRisk(row)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditRisk(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleRiskDelete(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormSection>

      <Dialog
        open={dialogOpen}
        onClose={savingAi ? undefined : handleDialogCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Data & Model Information</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'background.paper' }}>
          <FormSection title="Data sources">
            <EntityFormFields fields={DATA_MODEL_EXTRA_FIELDS} {...draftFieldProps} />
          </FormSection>
          <FormSection title="Data & model profile">
            <EntityFormFields
              fields={withCompactSelectFields(DATA_MODEL_INFO_FIELDS)}
              {...draftFieldProps}
            />
          </FormSection>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
          <Button onClick={handleDialogCancel} color="inherit" disabled={savingAi}>
            Cancel
          </Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={savingAi}>
            {savingAi ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={biasDialogOpen} onClose={handleBiasDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Known bias risk</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={5}
            label="Bias risk description"
            placeholder="Describe the particular bias risk for this system…"
            value={draftBiasDescription}
            onChange={(e) => setDraftBiasDescription(e.target.value)}
            error={Boolean(errors?.biasDescription)}
            helperText={errors?.biasDescription || ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleBiasDialogCancel} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleBiasDialogSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <SoftwareRiskDialog
        open={riskDialogOpen}
        initialValues={editingRisk}
        selectFromRegister
        excludeRiskRegisterIds={risks
          .filter((r) => r?.riskRegisterId != null && r?.key !== editingRisk?.key && r?.id !== editingRisk?.id)
          .map((r) => r.riskRegisterId)}
        onClose={() => {
          setRiskDialogOpen(false);
          setEditingRisk(null);
        }}
        onSave={handleRiskSave}
      />
    </Box>
  );
};

export default WizardStepRiskImpact;
